import { useEffect, useRef, useCallback, useState } from "react";
import { API_BASE } from "@/lib/api";
import { devWarn } from "@/lib/devLog";
import { playNotificationChime, unlockAudio } from "@/lib/beep";

// Kinds that should chime + push a browser notification.
const IMPORTANT_KINDS = new Set([
  "lead_assigned",
  "order_created",
  "order_ready",
  "order_status",
  "order_payment",
  "target_50",
  "target_last3",
  "security",
  "ai_nudge",
]);
function _isImportantKind(k) { return IMPORTANT_KINDS.has(k || ""); }

/**
 * Agent realtime hook.
 * - Opens a WebSocket to /api/ws/agent?token=<jwt> as soon as we have a token.
 * - Reconnects with exponential backoff.
 * - When a `notification` event arrives:
 *     • plays a sound
 *     • shows a browser notification (if permission granted + service worker)
 *     • invokes the provided onEvent callback (usually to refresh in-app state)
 */
export function useAgentRealtime({ enabled = true, onEvent } = {}) {
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const pingRef = useRef(null);
  const closedRef = useRef(false);
  // Keep the latest onEvent in a ref so the connect fn doesn't churn on every
  // parent render — otherwise the useEffect re-runs and the socket flaps.
  const onEventRef = useRef(onEvent);
  useEffect(() => { onEventRef.current = onEvent; }, [onEvent]);

  const connect = useCallback(() => {
    if (!enabled) return;
    // Derive ws(s):// URL from the HTTP API base.
    const sid = localStorage.getItem("crm_session_id") || "";
    const sidQs = sid ? `?session_id=${encodeURIComponent(sid)}` : "";
    const url = API_BASE.replace(/^http/, "ws") + `/ws/agent${sidQs}`;
    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        retryRef.current = 0;
        setConnected(true);
        if (pingRef.current) clearInterval(pingRef.current);
        pingRef.current = setInterval(() => {
          try { ws.send(JSON.stringify({ type: "ping" })); } catch (e) { devWarn("realtime.ping", e); }
        }, 25000);
      };

      ws.onmessage = async (evt) => {
        let msg = null;
        try { msg = JSON.parse(evt.data); } catch (e) { devWarn("realtime.parse", e); return; }
        if (!msg || !msg.type) return;
        try { onEventRef.current && onEventRef.current(msg); } catch (e) { devWarn("realtime.onEvent", e); }
        // Global event bus — lets any component subscribe cheaply.
        try { window.dispatchEvent(new CustomEvent("crm:realtime", { detail: msg })); } catch (e) { devWarn("realtime.dispatch", e); }
        if (msg.type === "notification" && msg.notification) {
          const n = msg.notification;
          try {
            // Only play the chime for IMPORTANT notification kinds so
            // low-signal system pings stay silent.
            const important = _isImportantKind(n.kind);
            if (important) playNotificationChime();
          } catch (e) { devWarn("realtime.chime", e); }
          showBrowserNotification(n);
        }
        if (msg.type === "force_logout") {
          try { window.dispatchEvent(new CustomEvent("crm:force-logout", { detail: msg })); } catch (e) { devWarn("realtime.forceLogout", e); }
        }
      };

      ws.onerror = () => {};
      ws.onclose = () => {
        if (wsRef.current !== ws) return;
        setConnected(false);
        if (pingRef.current) { clearInterval(pingRef.current); pingRef.current = null; }
        if (closedRef.current || !enabled) return;
        const delay = Math.min(30000, 800 * Math.pow(1.7, retryRef.current++));
        setTimeout(connect, delay);
      };
    } catch (_) {
      setTimeout(connect, 2000);
    }
  }, [enabled]);

  useEffect(() => {
    closedRef.current = false;
    connect();
    return () => {
      closedRef.current = true;
      const old = wsRef.current;
      wsRef.current = null;
      if (old) old.onclose = null;
      if (pingRef.current) clearInterval(pingRef.current);
      try { old && old.close(); } catch (e) { devWarn("realtime.close", e); }
    };
  }, [connect]);

  return { connected };
}

/** Request notification permission (safe to call multiple times). */
export async function ensureNotificationPermission() {
  if (typeof Notification === "undefined") return "unsupported";
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";
  try {
    const res = await Notification.requestPermission();
    return res;
  } catch (_) {
    return "denied";
  }
}

/** Register the PWA service worker (idempotent). */
export async function registerServiceWorker() {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register("/service-worker.js", { scope: "/" });
    return reg;
  } catch (_) {
    return null;
  }
}

/** Show a browser notification for an incoming realtime notification payload. */
async function showBrowserNotification(n) {
  if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
  const title = n.title || "New alert";
  const options = {
    body: n.body || "",
    tag: `northline-${n.id || n.lead_id || Date.now()}`,
    icon: "/icon-192.png",
    badge: "/icon-192.png",
    data: { url: n.lead_id ? `/app/leads/${n.lead_id}` : "/app/notifications", ...n },
    renotify: true,
    requireInteraction: false,
  };
  try {
    if ("serviceWorker" in navigator) {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) { await reg.showNotification(title, options); return; }
    }
    // Fallback (may not include click routing on mobile)
    // eslint-disable-next-line no-new
    new Notification(title, options);
  } catch (_) {
    // No-op — some browsers block notifications from insecure contexts.
  }
}

/** Prime the audio context inside a user gesture. */
export async function primeAudio() { return unlockAudio(); }
